<template>
  <div class="root-box">
    <el-row class="condition">
      <el-form size="small" :inline="true">
        <el-form-item label="路线：">
          <el-select v-model="param.pathId" placeholder="全部" clearable style="width: 180px">
            <el-option v-for="item in pathOpts" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="开始时间：">
          <el-date-picker v-model="param.startTime" type="datetime" placeholder="选择开始时间"
            value-format="yyyy-MM-dd HH:mm:ss" :default-time="'00:00:00'" style="width: 180px" clearable />
        </el-form-item>
        <el-form-item label="结束时间：">
          <el-date-picker v-model="param.endTime" type="datetime" placeholder="选择结束时间"
            value-format="yyyy-MM-dd HH:mm:ss" :default-time="'23:59:59'" style="width: 180px" clearable />
        </el-form-item>
        <el-form-item label="车辆品牌：">
          <el-input v-model="param.brand" placeholder="如：埃安、欧尚" clearable style="width: 120px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="query">查询</el-button>
        </el-form-item>
      </el-form>
    </el-row>

    <div class="chart-section">
      <el-card shadow="never">
        <div slot="header" class="chart-header">
          <span>耗电量统计图表</span>
          <el-radio-group v-model="chartDataKey" size="mini" @change="handleChartDataChange">
            <el-radio-button label="totalMiles">总里程(km)</el-radio-button>
            <el-radio-button label="totalBatteryConsumptionKWh">总耗电量(kWh)</el-radio-button>
            <el-radio-button label="avgConsumptionPerKm">单位里程平均耗电(kWh/km)</el-radio-button>
          </el-radio-group>
        </div>
        <Chart v-if="list.length > 0" ref="statsChart" :option="chartOption" :height="300" :on-load="onChartLoad" />
        <div v-else class="chart-empty">暂无数据，请先查询</div>
      </el-card>
    </div>

    <div class="table-section">
      <el-card shadow="never">
        <div slot="header">
          <span>按车辆统计</span>
        </div>
        <el-table :data="list" stripe style="width: 100%" show-summary :summary-method="getSummaries">
          <el-table-column label="序号" type="index" width="60" />
          <el-table-column prop="vehicleNo" label="车牌号" width="100" show-overflow-tooltip />
          <el-table-column prop="driverName" label="司机姓名" width="140" show-overflow-tooltip />
          <el-table-column prop="driverNo" label="司机编号" width="140" show-overflow-tooltip />
          <el-table-column prop="brand" label="品牌" width="100" show-overflow-tooltip />
          <el-table-column prop="routeCount" label="行程数" width="100" sortable />
          <el-table-column prop="totalMiles" label="总里程(km)" width="120" sortable>
            <template slot-scope="scope">
              {{ scope.row.totalMiles != null ? scope.row.totalMiles : "—" }}
            </template>
          </el-table-column>
          <el-table-column prop="totalBatteryConsumptionKWh" label="总耗电量(kWh)" sortable>
            <template slot-scope="scope">
              {{ scope.row.totalBatteryConsumptionKWh | numFormat(2) }}
            </template>
          </el-table-column>
          <el-table-column prop="avgConsumptionPerKm" label="平均耗电(kWh/km)" sortable>
            <template slot-scope="scope">
              {{ scope.row.avgConsumptionPerKm | numFormat(4) }}
            </template>
          </el-table-column>
          <el-table-column prop="totalPowerFee" label="总补贴电费(元)" sortable>
            <template slot-scope="scope">
              {{ scope.row.totalPowerFee | moneyFormat(2) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140" fixed="right">
            <template slot-scope="scope">
              <el-button v-if="scope.row.driverId" type="text" size="small" @click="openDetailDrawer(scope.row)">
                查看行程电量明细
              </el-button>
              <span v-else>—</span>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <el-drawer :visible.sync="detailDrawerVisible" title="行程电量明细" direction="rtl" size="1200px" :append-to-body="true"
      :before-close="closeDetailDrawer">
      <div v-if="detailDrawerVisible" class="detail-drawer">
        <div v-if="detailRow" class="detail-title">
          {{ detailRow.driverName || detailRow.driverNo || '—' }} · {{ detailRow.vehicleNo || '—' }}
        </div>
        <el-table :data="detailList" stripe size="small" height="calc(100vh - 280px)">
          <el-table-column label="序号" type="index" width="50" />
          <el-table-column prop="pathId" label="路线" width="160">
            <template slot-scope="scope">
              {{ scope.row.pathId | enumTransfer(pathOpts, 'id', 'name') }}
            </template>
          </el-table-column>
          <el-table-column prop="routeStartTime" label="行程开始" width="160">
            <template slot-scope="scope">
              {{ scope.row.routeStartTime | dateFormat('yyyy-MM-dd hh:mm') }}
            </template>
          </el-table-column>
          <el-table-column prop="routeEndTime" label="行程结束" width="160">
            <template slot-scope="scope">
              {{ scope.row.routeEndTime | dateFormat('yyyy-MM-dd hh:mm') }}
            </template>
          </el-table-column>

          <el-table-column prop="routeDuration" label="行程时长" sortable>
            <template slot-scope="scope">
              {{ scope.row.routeDuration | minutesToHours }}
            </template>
          </el-table-column>
          <el-table-column prop="totalMiles" label="里程(km)" sortable />
          <el-table-column prop="totalBatteryConsumptionKWh" label="耗电量(kWh)" sortable>
            <template slot-scope="scope">
              {{ formatDetailNum(scope.row.totalBatteryConsumptionKWh, 2) }}
            </template>
          </el-table-column>
          <el-table-column prop="powerFee" label="电费补贴(元)" sortable>
            <template slot-scope="scope">
              {{ formatDetailMoney(scope.row.powerFee) }}
            </template>
          </el-table-column>
          <el-table-column prop="routeId" label="操作" sortable width="160">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="showRoutePlan(scope.row)">行程规划</el-button>
              <el-button type="text" size="small" @click="go2QcPage(scope.row)"><span>质检详情</span></el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { listBatteryConsumptionStats, listBatteryConsumptionDetails } from "@/api";
import Chart from "@/components/Chart";
import { SHOW_ROUTE_PLAN } from "@/core/const/global-event";
import Router from "@/route";

const CHART_DATA_OPTIONS = {
  totalMiles: { label: "总里程(km)", unit: "km", getVal: (r) => r.totalMiles },
  totalBatteryConsumptionKWh: { label: "总耗电量(kWh)", unit: "kWh", getVal: (r) => r.totalBatteryConsumptionKWh },
  avgConsumptionPerKm: { label: "单位里程平均耗电(kWh/km)", unit: "kWh/km", getVal: (r) => r.avgConsumptionPerKm }
};

export default {
  name: "BatteryConsumptionStats",
  components: { Chart },

  filters: {
    minutesToHours(value) {
      if (value == null) return "—";
      const hours = Math.floor(value / 60);
      const minutes = value % 60;
      if (hours > 0) {
        return `${hours}小时${minutes}分钟`;
      } else {
        return `${minutes}分钟`;
      }
    }
  },
  data() {
    return {
      param: {
        pathId: null,
        startTime: null,
        endTime: null,
        brand: ""
      },
      list: [],
      chartDataKey: "avgConsumptionPerKm",
      chart: null,
      detailDrawerVisible: false,
      detailRow: null,
      detailList: []
    };
  },
  computed: {
    ...mapState({
      pathOpts: (state) => state.pathList
    }),
    chartOption() {
      if (!this.list || this.list.length === 0) {
        return { xAxis: { data: [] }, series: [{ data: [] }] };
      }
      const config = CHART_DATA_OPTIONS[this.chartDataKey] || CHART_DATA_OPTIONS.totalMiles;
      const labels = this.list.map((r) => r.vehicleNo || r.vin || r.vehicleId || "—");
      const data = this.list.map((r) => {
        const v = config.getVal(r);
        return v != null ? (typeof v === "number" && v % 1 !== 0 ? parseFloat(v.toFixed(4)) : v) : null;
      });
      return {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: (params) => {
            const p = params[0];
            const idx = p.dataIndex;
            const row = this.list[idx];
            const v = data[idx];
            return `${row.vehicleNo || row.vin || "—"}<br/>${config.label}: ${v != null ? v : "—"}`;
          }
        },
        grid: { left: "3%", right: "4%", bottom: "15%", containLabel: true },
        xAxis: {
          type: "category",
          data: labels,
          axisLabel: { rotate: 45, interval: 0 }
        },
        yAxis: {
          type: "value",
          name: config.label,
          axisLabel: { formatter: "{value}" }
        },
        series: [{ name: config.label, type: "bar", data }]
      };
    }
  },
  methods: {
    query() {
      const p = {
        pathId: this.param.pathId || undefined,
        startTime: this.param.startTime || undefined,
        endTime: this.param.endTime || undefined,
        brand: this.param.brand ? this.param.brand.trim() : undefined
      };
      listBatteryConsumptionStats(p).then((res) => {
        this.list = res.data || [];
        this.$nextTick(() => {
          if (this.$refs.statsChart) {
            this.$refs.statsChart.setOption(this.chartOption);
          }
        });
      });
    },
    handleChartDataChange() {
      this.$nextTick(() => {
        if (this.$refs.statsChart && this.chartOption) {
          this.$refs.statsChart.setOption(this.chartOption);
        }
      });
    },
    onChartLoad(chart) {
      this.chart = chart;
      if (this.list.length > 0) {
        this.$nextTick(() => {
          if (this.$refs.statsChart) {
            this.$refs.statsChart.setOption(this.chartOption);
          }
        });
      }
    },
    openDetailDrawer(row) {
      this.detailRow = row;
      this.detailDrawerVisible = true;
      this.detailList = [];
      const req = {
        driverId: row.driverId,
        pathId: this.param.pathId || undefined,
        startTime: this.param.startTime || undefined,
        endTime: this.param.endTime || undefined,
        brand: this.param.brand ? this.param.brand.trim() : undefined
      };
      listBatteryConsumptionDetails(req).then((res) => {
        this.detailList = res.data || [];
      });
    },
    closeDetailDrawer() {
      this.detailDrawerVisible = false;
      this.detailRow = null;
      this.detailList = [];
    },
    formatDetailNum(val, digits) {
      return val != null ? this.$options.filters.numFormat(val, digits) : "—";
    },
    formatDetailMoney(val) {
      return val != null ? this.$options.filters.moneyFormat(val, 2) : "—";
    },
    getSummaries({ columns, data }) {
      const sums = [];
      columns.forEach((col, index) => {
        if (index === 0) {
          sums.push("合计");
          return;
        }
        if (col.property === "routeCount") {
          const total = data.reduce((acc, row) => acc + (row.routeCount != null ? row.routeCount : 0), 0);
          sums.push(total || "—");
          return;
        }
        if (col.property === "totalMiles") {
          const total = data.reduce((acc, row) => acc + (row.totalMiles != null ? row.totalMiles : 0), 0);
          sums.push(total || "—");
          return;
        }
        if (col.property === "totalBatteryConsumptionKWh") {
          const total = data.reduce((acc, row) => acc + (row.totalBatteryConsumptionKWh != null ? row.totalBatteryConsumptionKWh : 0), 0);
          sums.push(total ? this.$options.filters.numFormat(total, 2) : "—");
          return;
        }
        if (col.property === "avgConsumptionPerKm") {
          const totalMiles = data.reduce((acc, row) => acc + (row.totalMiles != null ? row.totalMiles : 0), 0);
          const totalKWh = data.reduce((acc, row) => acc + (row.totalBatteryConsumptionKWh != null ? row.totalBatteryConsumptionKWh : 0), 0);
          sums.push(totalMiles > 0 ? this.$options.filters.numFormat(totalKWh / totalMiles, 4) : "—");
          return;
        }
        if (col.property === "totalPowerFee") {
          const total = data.reduce((acc, row) => acc + (row.totalPowerFee != null ? row.totalPowerFee : 0), 0);
          sums.push(total != null ? this.$options.filters.moneyFormat(total, 2) : "—");
          return;
        }
        sums.push("");
      });
      return sums;
    },
    showRoutePlan(item) {
      this.$bus.$emit(SHOW_ROUTE_PLAN, { id: item.routeId }, false);
    },

    go2QcPage(item) {
      const { href } = Router.resolve({
        path: "/route/qc",
        query: { routeId: item.routeId }
      });
      window.open(href, "_blank");
    },
  }
};
</script>

<style lang="less" scoped>
.root-box {
  padding: 20px;
}

.condition {
  background: #fff;
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 4px;
}

.chart-section {
  margin-bottom: 16px;

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .chart-empty {
    height: 300px;
    line-height: 300px;
    text-align: center;
    color: #909399;
  }
}

.table-section {
  background: #fff;
  padding: 0 16px 16px;
  border-radius: 4px;

  /deep/ .el-card__header {
    padding: 12px 16px;
  }
}

.detail-drawer {
  padding: 0 16px;

  .detail-title {
    margin-bottom: 16px;
    font-size: 15px;
    font-weight: 500;
  }
}
</style>
